import { isManualSource } from '../../domain/event-source';
import type { EventRecord } from '../../shared/contracts';
import { sourceMailResolverLink } from '../../domain/gmail-link';
import { Link, useLocation } from 'react-router-dom';

function displayCategory(event: EventRecord): { label: string; className: string } {
  if (event.deleted) return { label: '削除済み', className: 'deleted' };
  if (event.archived && event.archiveCategory === 'confirmed') return { label: '確認して残した予定', className: 'confirmed-done' };
  if (event.archived) return { label: '未確認の保管分', className: 'archived' };
  if (event.classification === '候補' || event.classification === '要確認') return { label: '要確認', className: 'review' };
  return { label: '予定', className: 'confirmed' };
}

function formatDate(record: EventRecord): string {
  if (record.allDay) return new Intl.DateTimeFormat('ja-JP', { month: 'numeric', day: 'numeric', weekday: 'short', timeZone: 'Asia/Tokyo' }).format(new Date(`${record.startDateTime}T00:00:00+09:00`)) + ' 終日';
  return new Intl.DateTimeFormat('ja-JP', { month: 'numeric', day: 'numeric', weekday: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tokyo' }).format(new Date(record.startDateTime));
}

function senderParts(value: string): { name: string; email: string } {
  const match = value.match(/^\s*"?([^"<]*)"?\s*<([^>]+)>\s*$/);
  if (!match) return value.includes('@') ? { name: '', email: value.trim() } : { name: value.trim(), email: '' };
  return { name: match[1].trim(), email: match[2].trim() };
}

export function EventCard({ event, selectable = true, selected, deleting, archiving, onSelect, onDelete, onArchive, onUnarchive, onIgnoreSource, onRestore, onEdit }: {
  event: EventRecord;
  selectable?: boolean;
  selected: boolean;
  deleting?: boolean;
  archiving?: boolean;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onArchive: (id: string) => void;
  onUnarchive?: (id: string) => void;
  onIgnoreSource: (event: EventRecord) => void;
  onRestore?: (id: string) => void;
  onEdit: (event: EventRecord) => void;
}) {
  const location = useLocation();
  const manual = isManualSource(event.messageId);
  const processing = Boolean(deleting || archiving);
  const pending = !event.calendarEventId;
  const category = displayCategory(event);
  const sender = senderParts(event.sender);
  return (
    <article className={`event-card ${selected ? 'selected' : ''} ${processing ? 'processing' : ''}`} aria-busy={processing}>
      {processing && <div className={`card-progress ${deleting ? 'deleting' : 'archiving'}`} role="status" aria-live="assertive">
        <span className="progress-spinner" aria-hidden="true" />
        <span><strong>{deleting ? '削除しています…' : '処理しています…'}</strong></span>
      </div>}
      {selectable && <label className="select-box" title="まとめて処理する予定を選ぶ" aria-label={`${event.title}をまとめて処理するため選択`}>
        <input type="checkbox" checked={selected} onChange={() => onSelect(event.id)} disabled={processing} />
        <span />
      </label>}
      <div className="event-body">
        <div className="event-meta"><div><span className={`classification ${category.className}`}>{category.label}</span>{event.calendarMovedAt && <span className="calendar-location">↗ {event.currentCalendarName || '他のカレンダー'}</span>}</div><time>{formatDate(event)}</time></div>
        <h3>{event.title}</h3>
        <p className="sender">{sender.name && <strong>{sender.name}</strong>}{sender.email && <small>{sender.email}</small>}</p>
        {(event.classification === '候補' || event.classification === '要確認') && <div className="review-reason"><span className="review-icon" aria-hidden="true">!</span><span><strong>ここを確認</strong>{event.reason}</span></div>}
        <div className={`card-actions ${event.deleted ? 'deleted-actions' : ''}`}>
          {manual ? <details className="quick-source"><summary>入力した内容</summary><p>{event.sourceText}</p></details> : <Link className="source-mail-link" to={sourceMailResolverLink(event.messageId)} state={{ sourceReturnTo: `${location.pathname}${location.search}`, sourceMailFallback: {
            subject: event.subject, sender: event.sender, threadId: event.threadId,
            receivedAt: event.receivedAt, sourceText: event.sourceText,
          } }}>元のメールを読む <span aria-hidden="true">→</span></Link>}
          {!event.deleted && <p className="calendar-decision-help">{pending ? '変更候補です。元の予定は変更していません。カレンダー未登録です。' : event.calendarMovedAt ? `「${event.currentCalendarName || '移動先のカレンダー'}」に登録済みです。` : 'カレンダーに登録済みです。'}{!event.archived && <strong>日時と内容は合っていますか？</strong>}</p>}
          <div className="card-actions-main decision-actions">
            {event.deleted ? <button className="button secondary" onClick={() => onRestore?.(event.id)}>{pending ? '候補を戻す' : 'カレンダーに戻す'}</button> : <>
              {!event.archived && !pending && <button className="button archive keep-event" onClick={() => onArchive(event.id)} disabled={processing}>このまま残す</button>}
              {event.archived && event.archiveCategory !== 'confirmed' && onUnarchive && <button className="button archive" onClick={() => onUnarchive(event.id)} disabled={processing}>確認する一覧へ戻す</button>}
              <button className="button secondary" onClick={() => onEdit(event)} disabled={processing}>{pending ? '内容を確認して登録' : '日時・内容を直す'}</button>
              <button className="button danger quiet-danger" onClick={() => onDelete(event.id)} disabled={processing}>{pending ? '候補を取り下げる' : 'カレンダーから消す'}</button>
            </>}
          </div>
          {!event.deleted && !event.archived && <p className="decision-later">あとで確認するときは、そのままで大丈夫です。</p>}
          <details className="card-more quiet-details">
            <summary>その他</summary>
            <div className="card-actions-secondary">
              {event.archived && event.archiveCategory === 'confirmed' && !event.deleted && onUnarchive && <button className="button ghost" onClick={() => onUnarchive(event.id)} disabled={processing}>確認する一覧へ戻す</button>}
              {!manual && <button className="button ignore-source" onClick={() => onIgnoreSource(event)} disabled={processing}>今後拾わない</button>}
            </div>
          </details>
        </div>
      </div>
    </article>
  );
}
